/**
 * SFCC-11961 — root-mounted Constructor.io trackers for the events the beacon
 * used to send (see `cio-sdk-tracking.ts`). Each renders null and is a no-op
 * while `Constructor_SdkTrackingEnabled` is off. Mount inside
 * `CIOClientContext` (root `App`).
 */
import { useEffect, useRef } from "react";
import { useFetchers, useLocation, useMatches } from "react-router";

import { pdpDataFromMatch } from "~/routes/pdp-data-from-match";
import type { CartUpdateEventData } from "~/segment/cart-update";

import { safeFireCioBeacon } from "./cio-listing-beacons";
import {
  cioSearchTerm,
  trackCioSessionStartOnce,
  useCioSdkTracker,
  useCioSdkTrackingEnabled,
} from "./cio-sdk-tracking";

/** Sends `session_start` for a new Constructor session once the SDK loads. */
export const CioSessionStartTracker = () => {
  const cio = useCioSdkTracker();
  useEffect(() => {
    if (cio) safeFireCioBeacon(() => trackCioSessionStartOnce(cio));
  }, [cio]);
  return null;
};

/** A conversion captured from a basket response, waiting for the SDK. */
type PendingConversion = { term: string; event: CartUpdateEventData };

/**
 * Add-to-cart `conversion`, one per added line, from the same basket-action
 * response `CartUpdateTracker` turns into `GA4_addToCart` — every add (PDP,
 * quick view, tiles, recs, ATC flyout, cross-sells) flows through
 * `/resources/basket`. Quantity edits on the cart page are not conversions.
 *
 * Fetcher handling mirrors `CartUpdateTracker`: the payload is only observable
 * while the fetcher is "loading", and a per-fetcher signature collapses
 * re-sightings into one send. Adds made before the deferred SDK chunk lands
 * are captured (with the term at add time) and sent once it does — the basket
 * fetcher is gone from `useFetchers()` by then.
 */
export const CioConversionTracker = () => {
  const enabled = useCioSdkTrackingEnabled();
  const cio = useCioSdkTracker();
  const fetchers = useFetchers();
  const firedRef = useRef<Map<string, string>>(new Map());
  const pendingRef = useRef<PendingConversion[]>([]);

  useEffect(() => {
    if (!enabled) return;
    for (const fetcher of fetchers) {
      if (fetcher.state === "submitting" || !fetcher.data) continue;
      const events = (
        fetcher.data as { segmentCartUpdate?: CartUpdateEventData[] }
      ).segmentCartUpdate;
      if (!events?.length || events[0].action !== "add") continue;

      const signature = JSON.stringify(events);
      if (firedRef.current.get(fetcher.key) === signature) continue;
      firedRef.current.set(fetcher.key, signature);

      const term = cioSearchTerm.resolve();
      for (const event of events) pendingRef.current.push({ term, event });
    }

    if (!cio || pendingRef.current.length === 0) return;
    const pending = pendingRef.current;
    pendingRef.current = [];
    for (const { term, event } of pending) {
      safeFireCioBeacon(() => {
        cio.tracker.trackConversion(term, {
          // `product_id` is the master id for variants (`sku` the variant),
          // matching the CIO catalog's item / variation ids.
          itemId: event.product_id,
          itemName: event.title,
          variationId:
            event.sku && event.sku !== event.product_id ? event.sku : undefined,
          // Per-unit price (`cart-update.ts`).
          revenue: event.price,
          type: "add_to_cart",
          section: "Products",
        });
      });
    }
  }, [enabled, cio, fetchers]);

  return null;
};

/**
 * Clears the conversion search term on navigation to an unrelated page: the
 * term survives the search results page and PDPs (reached from it) only.
 * Search / browse result loads store and clear it themselves
 * (`cio-listing-beacons.ts`), recommendation clicks clear it
 * (`use-track-recs-click.ts`).
 */
export const CioSearchTermScope = () => {
  const { pathname } = useLocation();
  const matches = useMatches();
  const isPdp = matches.some(m => pdpDataFromMatch(m) !== undefined);
  const isSearch = pathname === "/search";

  useEffect(() => {
    if (!isPdp && !isSearch) cioSearchTerm.clear();
  }, [pathname, isPdp, isSearch]);

  return null;
};
